import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SectionTrustBar from "../../components/feature/sections/SectionTrustBar";
import SectionAIBlock from "../../components/feature/sections/SectionAIBlock";
import SectionProcess from "../../components/feature/sections/SectionProcess";
import SectionWhyChooseUs from "../../components/feature/sections/SectionWhyChooseUs";
import SectionServiceAreas from "../../components/feature/sections/SectionServiceAreas";
import SectionTestimonials from "../../components/feature/sections/SectionTestimonials";
import SectionFAQ from "../../components/feature/sections/SectionFAQ";
import SectionFinalCTA from "../../components/feature/sections/SectionFinalCTA";
import { practiceAreasData } from "../../mocks/practiceAreasData";

const allAreas = [
  { slug: "health-law", label: "Health Law", icon: "ri-hospital-line" },
  { slug: "business-law", label: "Business Law", icon: "ri-building-2-line" },
  { slug: "litigation", label: "Litigation", icon: "ri-scales-line" },
  { slug: "contract-disputes", label: "Contract Disputes", icon: "ri-file-text-line" },
  { slug: "real-estate-law", label: "Real Estate Law", icon: "ri-home-office-line" },
  { slug: "medicare-compliance", label: "Medicare Compliance", icon: "ri-secure-payment-line" },
];

const listingAIQuestions = [
  {
    q: "What practice areas does this law firm cover?",
    a: "Alice G. Gosfield & Associates covers health law and regulation, Medicare and Medicaid compliance, business law for health care organizations, litigation and dispute resolution, contract disputes, and health care real estate law — a comprehensive suite of legal services for the health care industry.",
  },
  {
    q: "How do I know which practice area applies to my situation?",
    a: "Many legal matters cross practice area boundaries. A physician employment dispute may involve both business law and litigation. A Medicare audit may involve both compliance and litigation. We assess your complete situation during an initial consultation and identify the right legal strategy.",
  },
];

const listingFAQs = [
  {
    q: "What is the primary practice area for this firm?",
    a: "The firm's primary focus is health law — the legal practice area governing health care providers, health systems, and organizations. Within health law, we are nationally recognized for Medicare and Medicaid compliance, fraud and abuse avoidance, and managed care contracting.",
  },
  {
    q: "Do you handle non-health care legal matters?",
    a: "While our primary focus is health care law, we regularly assist health care organizations with general business legal matters including corporate governance, business contracts, employment law, and real estate transactions — all within the health care industry context.",
  },
  {
    q: "How quickly can I get help with a legal matter?",
    a: "We respond to all consultation requests within one business day. For urgent matters such as government investigations, audit deadlines, or emergency injunctive relief, we prioritize immediate response. Call (215) 735-2384 directly for time-sensitive matters.",
  },
];

function PracticeAreasListing() {
  return (
    <>
      {/* ① HERO */}
      <section className="relative min-h-[65vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/910bb3b2940b9eee38f5001bf05eed17.jpg"
            alt="Legal practice areas — Alice G. Gosfield &amp; Associates Philadelphia"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/94 via-[#0A1628]/72 to-[#0A1628]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32 pt-44">
          <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-4">
            Legal Services
          </span>
          <h1 className="text-5xl lg:text-[64px] font-playfair font-bold text-white leading-[1.06] max-w-2xl mb-6">
            Our Practice<br />
            <span className="italic text-[#C9A961]">Areas</span>
          </h1>
          <p className="text-slate-300 font-inter text-lg max-w-xl mb-8 leading-relaxed">
            Specialized health law counsel across the full spectrum of regulatory, compliance,
            transactional, and adversarial legal needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line" />
              Free Consultation
            </Link>
            <a
              href="tel:+12157352384"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#C9A961] text-white hover:text-[#C9A961] font-inter font-medium px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-[#C9A961]" />
              (215) 735-2384
            </a>
          </div>
        </div>
      </section>

      {/* ② TRUST BAR */}
      <SectionTrustBar />

      {/* ③ PRACTICE AREAS LISTING */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-3">
              Our Expertise
            </span>
            <h2 className="text-4xl lg:text-5xl font-playfair font-bold text-[#0A1628] mb-4">
              Areas of Legal Practice
            </h2>
            <p className="text-slate-500 font-inter text-base max-w-xl mx-auto">
              Click any practice area below to learn about our services, approach, and how we can help
              with your specific legal needs.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {allAreas.map((area) => {
              const data = practiceAreasData[area.slug];
              return (
                <Link
                  key={area.slug}
                  to={`/practice-areas/${area.slug}`}
                  className="group flex flex-col p-7 rounded-xl border border-slate-100 bg-[#FAFAFA] hover:bg-white hover:border-[#C9A961]/40 transition-all duration-300 cursor-pointer"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0A1628] mb-5 group-hover:bg-[#C9A961] transition-colors duration-300">
                    <i
                      className={`${area.icon} text-[#C9A961] text-xl group-hover:text-[#0A1628] transition-colors duration-300`}
                    />
                  </span>
                  <h2 className="text-xl font-playfair font-bold text-[#0A1628] mb-2.5">
                    {area.label}
                  </h2>
                  <p className="text-slate-500 text-sm font-inter leading-relaxed flex-1">
                    {data?.subtitle}
                  </p>
                  <div className="flex items-center gap-1.5 mt-5 text-sm font-inter font-semibold text-[#C9A961] group-hover:translate-x-1 transition-transform duration-200">
                    Learn More <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ④ AI BLOCK */}
      <SectionAIBlock questions={listingAIQuestions} title="Practice Areas — Answered" />
      {/* ⑤ PROCESS */}
      <SectionProcess />
      {/* ⑥ WHY CHOOSE US */}
      <SectionWhyChooseUs />
      {/* ⑦ SERVICE AREAS */}
      <SectionServiceAreas />
      {/* ⑧ TESTIMONIALS */}
      <SectionTestimonials />
      {/* ⑨ FAQ */}
      <SectionFAQ faqs={listingFAQs} title="Practice Areas FAQ" />
      {/* ⑩ FINAL CTA */}
      <SectionFinalCTA
        headline="Not Sure Which Practice Area Applies Today?"
        subheadline="Schedule a free consultation and we'll identify the right legal approach for your specific situation — no obligation, fully confidential."
      />
    </>
  );
}

function PracticeAreaDetail({ slug }: { slug: string }) {
  const data = practiceAreasData[slug];
  const [openSvc, setOpenSvc] = useState<number | null>(null);

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-playfair text-[#0A1628] mb-4">Practice Area Not Found</h2>
          <Link to="/practice-areas" className="text-[#C9A961] font-inter font-medium cursor-pointer">
            View All Practice Areas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Sub-nav */}
      <div className="bg-[#F8F7F4] border-b border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-1 overflow-x-auto py-2.5 scrollbar-hide">
            {allAreas.map((area) => (
              <Link
                key={area.slug}
                to={`/practice-areas/${area.slug}`}
                className={`px-4 py-2 rounded-full text-xs font-inter font-semibold whitespace-nowrap cursor-pointer transition-all ${
                  slug === area.slug
                    ? "bg-[#0A1628] text-[#C9A961]"
                    : "text-slate-600 hover:text-[#0A1628] hover:bg-white"
                }`}
              >
                {area.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ① HERO */}
      <section className="relative min-h-[65vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={data.heroImg}
            alt={`${data.title} legal services — Gosfield &amp; Associates Philadelphia`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/94 via-[#0A1628]/72 to-[#0A1628]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32 pt-12">
          <nav className="flex items-center gap-2 mb-5">
            <Link
              to="/practice-areas"
              className="text-slate-400 text-xs font-inter hover:text-[#C9A961] cursor-pointer transition-colors"
            >
              Practice Areas
            </Link>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs" />
            <span className="text-[#C9A961] text-xs font-inter">{data.title}</span>
          </nav>
          <h1 className="text-5xl lg:text-[60px] font-playfair font-bold text-white leading-[1.06] max-w-2xl mb-5">
            {data.title}<br />
            <span className="italic text-[#C9A961]">Legal Services</span>
          </h1>
          <p className="text-slate-300 font-inter text-lg max-w-xl mb-8 leading-relaxed">
            {data.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line" />
              Free Consultation
            </Link>
            <a
              href="tel:+12157352384"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#C9A961] text-white hover:text-[#C9A961] font-inter font-medium px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-[#C9A961]" />
              (215) 735-2384
            </a>
          </div>
        </div>
      </section>

      {/* ② TRUST BAR */}
      <SectionTrustBar />

      {/* ③ SERVICE BREAKDOWN */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
            <div>
              <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-3">
                Service Breakdown
              </span>
              <h2 className="text-4xl lg:text-5xl font-playfair font-bold text-[#0A1628] mb-3 leading-tight">
                How We Help
              </h2>
              <p className="text-slate-500 font-inter text-base max-w-xl leading-relaxed">
                {data.intro}
              </p>
            </div>
            <Link
              to="/contact"
              className="mt-6 lg:mt-0 inline-flex items-center gap-2 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-bold px-6 py-3 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              Discuss Your Matter
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.services.map((svc, i) => (
              <button
                key={i}
                onClick={() => setOpenSvc(openSvc === i ? null : i)}
                className={`flex flex-col p-6 rounded-xl border text-left transition-all duration-300 cursor-pointer ${
                  openSvc === i
                    ? "border-[#C9A961]/50 bg-[#FEFDFB]"
                    : "border-slate-100 bg-[#F8F7F4] hover:border-[#C9A961]/30 hover:bg-white"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-2 h-2 rounded-full bg-[#C9A961]" />
                  <i
                    className={`text-sm text-[#C9A961] transition-transform ${
                      openSvc === i ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  />
                </div>
                <h3
                  className="font-inter font-bold text-[#0A1628] text-sm mb-2"
                  dangerouslySetInnerHTML={{ __html: svc.title }}
                />
                {openSvc === i && (
                  <p className="text-slate-500 text-xs font-inter leading-relaxed mt-1">
                    {svc.desc}
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ④ AI ANSWER BLOCK */}
      <SectionAIBlock
        questions={data.aiQuestions}
        title={`${data.title} — Questions Answered`}
      />

      {/* ⑤ PROCESS */}
      <SectionProcess />

      {/* ⑥ WHY CHOOSE US */}
      <SectionWhyChooseUs />

      {/* ⑦ SERVICE AREAS */}
      <SectionServiceAreas />

      {/* ⑧ TESTIMONIALS */}
      <SectionTestimonials />

      {/* ⑨ FAQ */}
      <SectionFAQ
        faqs={data.faqs}
        title={`${data.title} FAQ`}
      />

      {/* ⑩ FINAL CTA */}
      <SectionFinalCTA
        headline={`Need ${data.title} Counsel Today?`}
        subheadline={`Contact our office to schedule a consultation with our experienced ${data.title.toLowerCase()} attorneys. Direct partner involvement, specialized expertise, results-driven guidance.`}
        ctaLabel="Schedule Consultation"
      />
    </>
  );
}

export default function PracticeAreaPage() {
  const { slug } = useParams<{ slug?: string }>();

  return (
    <div className="font-inter">
      <Navbar />
      <main>
        {slug ? <PracticeAreaDetail slug={slug} /> : <PracticeAreasListing />}
      </main>
      <Footer />
    </div>
  );
}
